import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info, AlertTriangle, TrendingUp, ChevronRight } from 'lucide-react';
import { useTranslation } from '@/components/utils/translations';
import MessagePerformanceInsights from '@/components/analytics/MessagePerformanceInsights';
import { appendAnalyticsWindow, coerceAnalyticsWindow } from '@/lib/analyticsWindow';

const API_BASE_URL = window.location.hostname === 'localhost'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

// Target-product filter — same shape as MostActiveTab so users get the
// identical control across tabs. Empty value = no filter.
const TARGET_PRODUCT_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'SYS', label: 'SYS' },
  { value: 'EXAM', label: 'EXAM' },
  { value: 'ALL', label: 'ALL (multi)' },
];

function TargetProductSwitch({ value, onChange }) {
  return (
    <div
      className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 self-start"
      role="group"
      aria-label="Target product filter"
    >
      {TARGET_PRODUCT_OPTIONS.map(({ value: optionValue, label }) => (
        <button
          key={optionValue || 'all'}
          type="button"
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === optionValue
              ? 'bg-white text-[#0A1A2F] shadow-sm'
              : 'text-gray-600 hover:text-[#0A1A2F]'
          }`}
          onClick={() => onChange(optionValue)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

const PERSONA_SIGNAL_PREFIX = 'persona: ';

function isPersonaSignalType(signalType) {
  return String(signalType || '').startsWith(PERSONA_SIGNAL_PREFIX);
}

function displaySignalTypeLabel(signalType) {
  const raw = String(signalType || '');
  if (isPersonaSignalType(raw)) {
    return raw.slice(PERSONA_SIGNAL_PREFIX.length).trim();
  }
  return raw;
}

function SignalTypeLabel({ signalType }) {
  const isPersona = isPersonaSignalType(signalType);
  const chipClass =
    'text-[10px] px-1.5 py-0 font-normal shrink-0 border-gray-300 text-gray-600';
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{displaySignalTypeLabel(signalType)}</span>
      <Badge variant="outline" className={chipClass}>
        {isPersona ? 'Persona' : 'Account'}
      </Badge>
    </span>
  );
}

function MatrixHeatmap({ title, matrixData, rateField, sendsField, colorRanges, formatRate }) {
  if (matrixData.matrix.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-gray-500 text-center py-8">Not enough data for matrix</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          {title}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">
                  Scanner-filtered: opens/clicks where the user-agent and IP were classified as a real human.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left p-2 border-b">Signal type</th>
                {matrixData.archetypes.map((arch) => (
                  <th key={arch} className="text-center p-2 border-b text-xs">{arch}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrixData.signal_types.map((signalType) => (
                <tr key={signalType}>
                  <td className="p-2 border-b font-medium">
                    <SignalTypeLabel signalType={signalType} />
                  </td>
                  {matrixData.archetypes.map((arch) => {
                    const cell = matrixData.matrix.find(
                      (m) => m.signal_type === signalType && m.archetype === arch
                    );
                    const rate = Number(cell?.[rateField] ?? 0);
                    const sends = Number(cell?.[sendsField] ?? 0);
                    const bgColor =
                      sends === 0 ? 'bg-gray-50' :
                      rate === 0 ? 'bg-gray-100' :
                      rate < colorRanges.low ? 'bg-yellow-100' :
                      rate < colorRanges.high ? 'bg-green-100' :
                      'bg-green-200';
                    return (
                      <td key={arch} className={`p-2 border-b text-center ${bgColor}`}>
                        {!cell || sends === 0 ? '—' : formatRate(rate)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SignalsTab({ tenantId, timeRange, analyticsWindow, userId }) {
  const { t } = useTranslation();
  const [signalData, setSignalData] = useState([]);
  const [matrixData, setMatrixData] = useState({ matrix: [], signal_types: [], archetypes: [] });
  const [topAccounts, setTopAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSignal, setSelectedSignal] = useState(null);
  
  // Filters
  const [tierFilter, setTierFilter] = useState('All');
  const [archetypeFilter, setArchetypeFilter] = useState('All');
  const [channelFilter, setChannelFilter] = useState('All');
  const [targetProductFilter, setTargetProductFilter] = useState('');

  const SORT_FIELDS = {
    click_rate: 'click_rate',
    open_rate: 'open_rate',
    reply_rate: 'reply_rate',
    accounts: 'accounts_with_signal',
    touched: 'accounts_touched',
    personas: 'personas_touched',
    sample: 'sample_size',
    signal: 'signal_type',
  };
  const [sortKey, setSortKey] = useState('click_rate');
  const [sortDir, setSortDir] = useState('desc');

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'signal' ? 'asc' : 'desc');
    }
  }

  const NUMERIC_SORT_KEYS = new Set([
    'click_rate',
    'open_rate',
    'reply_rate',
    'accounts',
    'touched',
    'personas',
    'sample',
  ]);

  const sortedSignalData = useMemo(() => {
    const field = SORT_FIELDS[sortKey];
    const dir = sortDir === 'asc' ? 1 : -1;
    const isNumeric = NUMERIC_SORT_KEYS.has(sortKey);
    const out = [...signalData];

    out.sort((a, b) => {
      if (sortKey === 'click_rate' || sortKey === 'open_rate') {
        const aHas = Number(a?.email_sends || 0) > 0 ? 1 : 0;
        const bHas = Number(b?.email_sends || 0) > 0 ? 1 : 0;
        if (aHas !== bHas) return bHas - aHas;
      }
      if (sortKey === 'reply_rate') {
        const aHas = Number(a?.accounts_touched || a?.sample_size || 0) > 0 ? 1 : 0;
        const bHas = Number(b?.accounts_touched || b?.sample_size || 0) > 0 ? 1 : 0;
        if (aHas !== bHas) return bHas - aHas;
      }

      const av = a?.[field];
      const bv = b?.[field];

      if (isNumeric) {
        const an = Number.isFinite(Number(av)) ? Number(av) : -Infinity;
        const bn = Number.isFinite(Number(bv)) ? Number(bv) : -Infinity;
        return (an - bn) * dir;
      }
      return String(av ?? '').localeCompare(String(bv ?? ''), undefined, { sensitivity: 'base' }) * dir;
    });
    return out;
  }, [signalData, sortKey, sortDir]);

  const sortMark = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  useEffect(() => {
    loadSignalData();
  }, [tenantId, timeRange, analyticsWindow, userId, tierFilter, archetypeFilter, channelFilter, targetProductFilter]);

  useEffect(() => {
    if (selectedSignal) {
      loadTopAccounts(selectedSignal);
    }
  }, [selectedSignal, userId, targetProductFilter]);
  
  const loadSignalData = async () => {
    setLoading(true);
    try {
      // Load signal type performance
      const params = new URLSearchParams({
        tenant_id: tenantId,
        days: timeRange,
        ...(tierFilter !== 'All' && { tier: tierFilter }),
        ...(archetypeFilter !== 'All' && { archetype: archetypeFilter }),
        ...(channelFilter !== 'All' && { channel: channelFilter }),
        ...(targetProductFilter && { target_products: targetProductFilter }),
      });
      appendAnalyticsWindow(params, coerceAnalyticsWindow(analyticsWindow ?? timeRange));
      if (userId) params.append('user_id', userId);
      
      const response = await fetch(
        `${API_BASE_URL}/api/analytics-v2/signals/by-type?${params}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSignalData(data);
      }
      
      // Load matrix data
      const matrixParams = new URLSearchParams({
        tenant_id: tenantId,
        days: timeRange,
        tier: tierFilter,
        ...(targetProductFilter && { target_products: targetProductFilter }),
      });
      appendAnalyticsWindow(matrixParams, coerceAnalyticsWindow(analyticsWindow ?? timeRange));
      if (userId) matrixParams.append('user_id', userId);
      const matrixResponse = await fetch(
        `${API_BASE_URL}/api/analytics-v2/signals/by-archetype?${matrixParams.toString()}`,
        { credentials: 'include' }
      );
      
      if (matrixResponse.ok) {
        const matrix = await matrixResponse.json();
        setMatrixData(matrix);
      }
      
    } catch (error) {
      console.error('Error loading signal data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadTopAccounts = async (signalType) => {
    try {
      const topParams = new URLSearchParams({
        tenant_id: tenantId,
        signal_type: signalType,
        days: timeRange,
        ...(targetProductFilter && { target_products: targetProductFilter }),
      });
      appendAnalyticsWindow(topParams, coerceAnalyticsWindow(analyticsWindow ?? timeRange));
      if (userId) topParams.append('user_id', userId);
      const response = await fetch(
        `${API_BASE_URL}/api/analytics-v2/signals/top-accounts?${topParams.toString()}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTopAccounts(data);
      }
    } catch (error) {
      console.error('Error loading top accounts:', error);
    }
  };

  const formatRate = (rate, sends) => {
    const sendsNum = Number(sends || 0);
    const rateNum = Number(rate);
    if (!Number.isFinite(rateNum) || sendsNum <= 0) return '—';
    return `${rateNum.toFixed(1)}%`;
  };
  
  return (
    <div className="space-y-6">
      <MessagePerformanceInsights tenantId={tenantId} timeRange={timeRange} analyticsWindow={analyticsWindow} userId={userId} />

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Target product:</span>
              <TargetProductSwitch value={targetProductFilter} onChange={setTargetProductFilter} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t('tier')}:</span>
              <Select value={tierFilter} onValueChange={setTierFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">{t('allTiers')}</SelectItem>
                  <SelectItem value="T1">T1</SelectItem>
                  <SelectItem value="T2">T2</SelectItem>
                  <SelectItem value="T3">T3</SelectItem>
                  <SelectItem value="T4">T4</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t('channel')}:</span>
              <Select value={channelFilter} onValueChange={setChannelFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">{t('allChannels')}</SelectItem>
                  <SelectItem value="email">{t('email')}</SelectItem>
                  <SelectItem value="linkedin">{t('linkedin')}</SelectItem>
                  <SelectItem value="call">{t('call')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Signal Type Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#00C2C2]" />
            {t('signalTypePerformance')}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="w-4 h-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">{t('signalTypePerformanceTooltip')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="animate-spin w-8 h-8 border-2 border-[#00C2C2] border-t-transparent rounded-full"></div>
            </div>
          ) : signalData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-500">
              {t('noSignalDataSelectedPeriod')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    onClick={() => toggleSort('signal')}
                    className="cursor-pointer select-none"
                  >
                    {t('signalType')}
                    {sortMark('signal')}
                  </TableHead>
                  <TableHead
                    onClick={() => toggleSort('accounts')}
                    className="text-right cursor-pointer select-none"
                  >
                    {t('accounts')}
                    {sortMark('accounts')}
                  </TableHead>
                  <TableHead
                    onClick={() => toggleSort('touched')}
                    className="text-right cursor-pointer select-none"
                  >
                    {t('touched')}
                    {sortMark('touched')}
                  </TableHead>
                  <TableHead
                    onClick={() => toggleSort('personas')}
                    className="text-right cursor-pointer select-none"
                  >
                    {t('personas')}
                    {sortMark('personas')}
                  </TableHead>
                  <TableHead
                    onClick={() => toggleSort('reply_rate')}
                    className="text-right cursor-pointer select-none"
                  >
                    {t('replyRate')}
                    {sortMark('reply_rate')}
                  </TableHead>
                  <TableHead
                    onClick={() => toggleSort('open_rate')}
                    className="text-right cursor-pointer select-none"
                  >
                    <div className="inline-flex items-center justify-end gap-1 w-full">
                      Open rate
                      {sortMark('open_rate')}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-gray-400 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs">
                              Scanner-filtered: opens/clicks where the user-agent and IP were classified as a real human.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableHead>
                  <TableHead
                    onClick={() => toggleSort('click_rate')}
                    className="text-right cursor-pointer select-none"
                  >
                    <div className="inline-flex items-center justify-end gap-1 w-full">
                      Click rate
                      {sortMark('click_rate')}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-gray-400 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs">
                              Scanner-filtered: opens/clicks where the user-agent and IP were classified as a real human.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableHead>
                  <TableHead className="text-right">{t('bestChannel')}</TableHead>
                  <TableHead
                    onClick={() => toggleSort('sample')}
                    className="text-right cursor-pointer select-none"
                  >
                    {t('sample')}
                    {sortMark('sample')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSignalData.map((signal, idx) => (
                  <TableRow 
                    key={idx}
                    className={`cursor-pointer hover:bg-gray-50 ${selectedSignal === signal.signal_type ? 'bg-[#00C2C2]/10' : ''}`}
                    onClick={() => setSelectedSignal(signal.signal_type)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <SignalTypeLabel signalType={signal.signal_type} />
                        {signal.low_sample && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">{t('lowSampleSize')}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{signal.accounts_with_signal}</TableCell>
                    <TableCell className="text-right">{signal.accounts_touched}</TableCell>
                    <TableCell className="text-right">{signal.personas_touched}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={signal.reply_rate > 10 ? 'default' : 'secondary'} 
                             className={signal.reply_rate > 10 ? 'bg-green-100 text-green-800' : ''}>
                        {signal.reply_rate}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-xs text-gray-700">
                      <div className="font-medium">{formatRate(signal.open_rate, signal.email_sends)}</div>
                      {Number(signal.email_sends || 0) > 0 && (
                        <div className="text-gray-500">of {signal.email_sends}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs text-gray-700">
                      <div className="font-medium">{formatRate(signal.click_rate, signal.email_sends)}</div>
                      {Number(signal.email_sends || 0) > 0 && (
                        <div className="text-gray-500">of {signal.email_sends}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{signal.best_channel}</TableCell>
                    <TableCell className="text-right text-gray-500">{signal.sample_size}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Top Accounts Panel (shows when signal selected) */}
      {selectedSignal && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {t('topResponsiveAccountsFor')} "{selectedSignal}"
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topAccounts.length === 0 ? (
              <div className="text-gray-500 text-center py-8">{t('noAccountsTouchedForSignal')}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('account')}</TableHead>
                    <TableHead>{t('tier')}</TableHead>
                    <TableHead className="text-right">{t('personas')}</TableHead>
                    <TableHead className="text-right">{t('replies')}</TableHead>
                    <TableHead className="text-right">{t('replyRate')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topAccounts.map((account, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{account.account_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{account.account_tier || t('na')}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{account.personas_touched}</TableCell>
                      <TableCell className="text-right">{account.replies}</TableCell>
                      <TableCell className="text-right">
                        <Badge className={account.reply_rate > 0 ? 'bg-green-100 text-green-800' : ''}>
                          {account.reply_rate}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Signal x Archetype — Open rates (scanner-filtered) */}
      <MatrixHeatmap
        title={t('signalArchetypeOpenRates')}
        matrixData={matrixData}
        rateField="open_rate"
        sendsField="email_sends"
        colorRanges={{ low: 30, high: 50 }}
        formatRate={(rate) => `${rate.toFixed(1)}%`}
      />

      {/* Signal x Archetype — Click rates (scanner-filtered) */}
      <MatrixHeatmap
        title={t('signalArchetypeClickRates')}
        matrixData={matrixData}
        rateField="click_rate"
        sendsField="email_sends"
        colorRanges={{ low: 3, high: 8 }}
        formatRate={(rate) => `${rate.toFixed(1)}%`}
      />

      {/* Signal x Archetype — Reply rates (existing) */}
      <MatrixHeatmap
        title={t('signalArchetypeReplyRates')}
        matrixData={matrixData}
        rateField="reply_rate"
        sendsField="touched"
        colorRanges={{ low: 10, high: 20 }}
        formatRate={(rate) => `${rate}%`}
      />
    </div>
  );
}